<script lang="ts" setup>
const emit = defineEmits<{
  change: []
}>()

const { isBusinessAdmin, isBusinessMember, canCreateSignatureRequest } = storeToRefs(useUserStore())

const { t } = useI18n()

const route = useRoute()

const { activePricePlanId } = useUserPlanInfo()

const isUploadAllowed = computed(() => isBusinessAdmin.value || isBusinessMember.value)

const isNewDisabled = computed(() => !isUploadAllowed.value || !canCreateSignatureRequest.value)

const { createBlockedTooltip } = useCreateBlockedTooltip()

const goToAdmin = computed(() => {
  if (['TRIAL', 'TEAM', 'PRO', 'SCALE'].some(plan => activePricePlanId.value?.startsWith(plan))) {
    return 'business-dashboard'
  } else if (activePricePlanId.value?.startsWith('INDIVIDUAL')) {
    return 'business-billing-plan'
  } else {
    return 'business-members'
  }
})

const { isPlanPageFlag, isAdminAreaFlag } = useNavigationFlags()
</script>

<template>
  <!--
    TODO: This and MainNav.vue are very similar and should be
    refactored for less duplication.
    Maybe even consider MainNavBusiness.vue and MiniNavBusiness.vue too.
  -->

  <!--
    Note: the mini nav gets a simulated clipped applied by styling through
    the has-alert class on the application tag. We just add the margin-top
    and the height with important to overwrite the inline styling set by
    Vuetify.

    Adding the clipped attribute thus has no effect on this element.
  -->
  <v-navigation-drawer floating class="mini-nav" rail rail-width="90" :mobile-breakpoint="600">
    <div class="d-flex flex-column pb-2 | mini-nav__flex">
      <v-app-bar-nav-icon
        :ripple="false"
        class="mini-nav__trigger"
        data-cy="mini_navigation_menu"
        @click.stop="emit('change')"
      >
        <v-icon size="x-large">custom:menu</v-icon>
      </v-app-bar-nav-icon>
      <v-list class="py-0" nav>
        <v-tooltip content-class="text-center bg-info" location="right" max-width="207" :disabled="!isNewDisabled">
          <template #activator="{ props: activatorProps }">
            <div v-bind="activatorProps">
              <v-list-item
                :disabled="isNewDisabled"
                :ripple="false"
                class="mini-nav__new"
                data-cy="create_doc_button"
                exact
                :to="{ name: 'upload' }"
              >
                <v-list-item-action class="align-center flex-column my-3">
                  <v-icon size="40" :color="isNewDisabled ? 'grey-bg-4' : 'primary'">custom:new_btn_desktop</v-icon>
                  <span class="mini-nav__label">
                    {{ t('global.navigation.new') }}
                  </span>
                </v-list-item-action>
              </v-list-item>
            </div>
          </template>
          <span>{{ createBlockedTooltip }}</span>
        </v-tooltip>
        <v-list-item
          :ripple="false"
          :active="route.name === 'index' && route.query.tab === 'inbox'"
          color="primary"
          data-cy="mini_navigation_inbox"
          :to="{ name: 'index', query: { tab: 'inbox' } }"
        >
          <v-list-item-action class="align-center flex-column my-3">
            <div class="mini-nav__border" />
            <v-icon>custom:folder_user</v-icon>
            <span class="mini-nav__label">
              {{ t('global.navigation.inbox') }}
            </span>
          </v-list-item-action>
        </v-list-item>
        <v-list-item
          :ripple="false"
          :active="route.query.tab === 'drafts'"
          color="primary"
          data-cy="mini_navigation_drafts"
          :to="{ name: 'index', query: { tab: 'drafts' } }"
        >
          <v-list-item-action class="align-center flex-column my-3">
            <div class="mini-nav__border" />
            <v-icon>custom:draft</v-icon>
            <span class="mini-nav__label">
              {{ t('global.navigation.drafts') }}
            </span>
          </v-list-item-action>
        </v-list-item>
        <skr-divider v-if="isPlanPageFlag" class="mx-3" />
        <v-list-item
          v-if="isPlanPageFlag"
          :ripple="false"
          color="primary"
          data-cy="mini_navigation_plan"
          class="mini-nav__plan"
          :to="{ name: 'plan' }"
        >
          <v-list-item-action class="align-center flex-column my-3">
            <div class="mini-nav__border" />
            <v-icon>custom:starter_plan</v-icon>
            <span class="mini-nav__label mini-nav__plan__label"> {{ t('global.navigation.my_plan') }}</span>
          </v-list-item-action>
        </v-list-item>
      </v-list>

      <v-list class="mt-auto py-0" nav>
        <v-list-item
          v-if="isAdminAreaFlag"
          :ripple="false"
          color="primary"
          data-cy="mini_navigation_admin"
          exact
          :to="{ name: goToAdmin }"
        >
          <v-list-item-action class="align-center flex-column my-3">
            <v-icon>custom:business_account</v-icon>
            <span class="mini-nav__label">
              {{ t('global.navigation.admin_area') }}
            </span>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </div>
  </v-navigation-drawer>
</template>

<style lang="scss">
.mini-nav {
  font-size: 0.75rem;
  font-weight: bold;

  .v-list--nav {
    padding: 0px;
  }

  .v-list-item {
    padding-left: 0;
    padding-right: 0;
  }

  &:not(.business) {
    .v-list-item {
      &.v-theme--light {
        @include mini-nav-item-hover;
        margin: 4px 10px 4px 10px;
        &:not(.v-list-item--active) {
          &:hover {
            background: rgba(151, 151, 151, 0.1);
            .v-list-item-action,
            .v-icon {
              color: $c-grey-dark;
            }
          }
        }
        &.v-list-item--active {
          &:hover {
            .v-list-item__overlay {
              opacity: 0.12;
            }
            background: inherit;
          }
        }
        &.mini-nav__new {
          &:before {
            top: 9px;
            width: 46px;
            height: 46px;
          }
          &:not(.v-list-item--disabled) span {
            color: $c-primary;
          }
          .v-list-item--disabled span {
            color: $c-grey-bg-4;
          }
          .v-list-item--active {
            color: $c-primary;
          }
        }
      }
      &__action:first-child {
        margin-left: 0;
        margin-right: 0;
      }
    }

    .mini-nav__flex {
      height: 100%;
      background: $c-grey-light-3;
    }
  }

  &__trigger {
    margin-bottom: 20px;
    width: 100% !important;
    height: 90px !important;
    border-radius: 0;
    span {
      i {
        height: 26px !important;
        font-size: 34px !important;
        width: 26px !important;
      }
    }
    &:hover {
      background: rgba(151, 151, 151, 0.1);
    }
  }

  &__label {
    margin-top: 9px;
    padding-left: 4px;
    padding-right: 4px;
    text-align: center;
    white-space: normal;
    line-height: 1.2;
  }

  &__signing-status {
    position: absolute;
    right: 18px;
    top: -2px;
    width: 16px;
    height: 16px;
  }

  &__plan {
    &.v-list-item {
      &:hover {
        > .v-list-item__overlay {
          background: $c-purple-bg;
        }
      }
      &--active {
        > .v-list-item__content,
        .v-icon {
          color: $c-purple-dark;
        }
        > .v-list-item__overlay {
          background: $c-purple-bg;
          opacity: 1;
          transition: background-color 0.3s ease;
        }
        &:hover {
          > .v-list-item__overlay {
            background: $c-purple-bg;
            opacity: 1 !important;
          }
          > .v-list-item__content,
          .v-icon {
            color: $c-purple-dark;
          }
        }
      }
    }
    &__label {
      z-index: 1;
    }
  }
}
</style>
